import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import BuyerPublicNavbar from "@/components/BuyerPublicNavbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Buildings,
  MagnifyingGlass,
  Faders,
  Storefront,
  MapPin,
  ChartBar,
  SpinnerGap,
  X,
} from "@phosphor-icons/react";
import api, { publicAssetUrl } from "@/lib/api";
import { scaffoldingTypeBadges } from "@/lib/supplierTypeLabels";
import CityListAutocomplete from "@/components/CityListAutocomplete";

const selectTriggerCls = "h-10 rounded-xl text-sm focus:ring-2 focus:ring-[#FF7A00] focus:ring-offset-0";
const selectTriggerStyle = { background: "#FDFCF9", border: "1.5px solid #E8E3D9", color: "#444" };

function FilterLabel({ children }) {
  return (
    <label className="block text-[10px] font-bold tracking-[0.15em] uppercase mb-1.5" style={{ color: "#999" }}>
      {children}
    </label>
  );
}

function SupplierCardLogo({ logoUrl, name }) {
  const [broken, setBroken] = useState(false);
  const showPlaceholder = !logoUrl || broken;
  const initials = String(name || "S")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
  return (
    <div
      className="h-[160px] flex items-center justify-center border-b px-6"
      style={{ background: "rgba(255,122,0,0.04)", borderColor: "#F0EBE3" }}
    >
      {showPlaceholder ? (
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center font-black text-xl shrink-0"
          style={{ background: "rgba(255,122,0,0.12)", color: "#FF7A00" }}
        >
          {initials || <Buildings size={28} weight="duotone" />}
        </div>
      ) : (
        <img
          src={publicAssetUrl(logoUrl)}
          alt=""
          className="max-h-[120px] max-w-full object-contain rounded-xl"
          onError={() => setBroken(true)}
        />
      )}
    </div>
  );
}

export default function SupplierListingPage() {
  const [cities, setCities] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersTotal, setSuppliersTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [selectedCities, setSelectedCities] = useState([]);
  const [supplierType, setSupplierType] = useState("all");
  const [sort, setSort] = useState("name_asc");

  useEffect(() => {
    api.get("/public/cities").then((r) => setCities(r.data)).catch(() => setCities([]));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      query.set("limit", "200");
      query.set("sort", sort);
      selectedCities.forEach((c) => query.append("city", c));
      if (supplierType !== "all") query.set("supplier_type", supplierType);
      if (debouncedQ) query.set("q", debouncedQ);
      const r = await api.get(`/public/suppliers?${query.toString()}`);
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      const total = Array.isArray(r.data)
        ? items.length
        : Number(r.data?.total ?? items.length);
      setSuppliers(items);
      setSuppliersTotal(Number.isFinite(total) ? total : items.length);
    } catch {
      toast.error("Could not load suppliers");
      setSuppliers([]);
      setSuppliersTotal(0);
    }
    setLoading(false);
  }, [selectedCities, supplierType, debouncedQ, sort]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const clearFilters = () => {
    setSearchInput("");
    setDebouncedQ("");
    setCitySearch("");
    setSelectedCities([]);
    setSupplierType("all");
    setSort("name_asc");
  };

  const handleCitySelect = (selected) => {
    const normalized = String(selected || "").trim();
    if (!normalized) return;
    setSelectedCities((prev) => {
      if (prev.some((city) => city.toLowerCase() === normalized.toLowerCase())) return prev;
      return [...prev, normalized];
    });
    setCitySearch("");
  };

  const removeSelectedCity = (city) => {
    setSelectedCities((prev) => prev.filter((c) => c !== city));
  };

  const clearCityFilter = () => {
    setSelectedCities([]);
    setCitySearch("");
  };

  const activeFilterCount =
    (selectedCities.length > 0 ? 1 : 0) +
    (supplierType !== "all" ? 1 : 0) +
    (debouncedQ ? 1 : 0) +
    (sort !== "name_asc" ? 1 : 0);

  return (
    <div className="min-h-screen" style={{ background: "#F7F4EF" }}>
      <BuyerPublicNavbar
        testId="supplier-listing-navbar"
        showPostRequirement={false}
        brandSuffix={
          <span className="hidden lg:flex items-center gap-1.5 text-sm font-semibold text-neutral-500 border-l border-neutral-200 pl-4 ml-1">
            <Storefront size={18} weight="bold" className="text-[#FF7A00]" />
            Supplier directory
          </span>
        }
      />

      {/* ── HERO BANNER ─────────────────────────────────── */}
      <div
        className="relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #111 0%, #1a1a1a 100%)",
          borderBottom: "1px solid #2a2a2a",
        }}
      >
        {/* Grid texture */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-12 sm:py-16">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div>
              <p className="text-[10px] font-bold tracking-[0.25em] uppercase mb-3" style={{ color: "#FF7A00" }}>
                Verified network
              </p>
              <h1
                className="font-black text-white leading-none tracking-tighter"
                style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)" }}
              >
                Browse
                <br />
                <span style={{ color: "#FF7A00" }}>Suppliers</span>
              </h1>
              <p className="mt-4 text-sm sm:text-base leading-relaxed max-w-xl" style={{ color: "#aaa" }}>
                Filter by city, business type, or name. All partners are verified and registered on the platform.
              </p>
            </div>
            <div className="flex flex-col items-start sm:items-end gap-2 shrink-0">
              {loading ? (
                <span className="inline-flex items-center gap-2 text-sm" style={{ color: "#888" }}>
                  <SpinnerGap className="animate-spin" size={16} />
                  Loading…
                </span>
              ) : (
                <div
                  className="px-5 py-3 rounded-2xl"
                  style={{ background: "rgba(255,255,255,0.06)", border: "1px solid #2a2a2a" }}
                >
                  <p className="text-xs font-bold tracking-wider uppercase" style={{ color: "#888" }}>Suppliers found</p>
                  <p className="font-black text-white text-3xl leading-none mt-0.5" data-testid="supplier-result-count">
                    {suppliersTotal}
                  </p>
                </div>
              )}
              <Link
                to="/account/login"
                className="text-xs font-semibold underline underline-offset-2 transition-colors hover:text-white"
                style={{ color: "#888" }}
              >
                Are you a supplier? Register →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ── CONTENT ─────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
        <div className="lg:grid lg:grid-cols-[260px_1fr] lg:gap-8 items-start">

          {/* ── FILTERS SIDEBAR ─────────────────────────── */}
          <aside className="mb-6 lg:mb-0 lg:sticky lg:top-24" data-testid="supplier-filters">
            <div
              className="rounded-2xl overflow-visible"
              style={{ background: "#fff", border: "1px solid #E8E3D9", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}
            >
              {/* Sidebar header */}
              <div
                className="flex items-center justify-between px-5 py-4 border-b"
                style={{ borderColor: "#F0EBE3" }}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center"
                    style={{ background: "rgba(255,122,0,0.1)" }}
                  >
                    <Faders size={14} weight="bold" style={{ color: "#FF7A00" }} />
                  </div>
                  <span className="font-black text-sm text-neutral-900">Filters</span>
                  {activeFilterCount > 0 && (
                    <span
                      className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black"
                      style={{ background: "#FF7A00", color: "#fff" }}
                    >
                      {activeFilterCount}
                    </span>
                  )}
                </div>
                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="inline-flex items-center gap-1 text-xs font-bold transition-colors hover:opacity-70"
                    style={{ color: "#FF7A00" }}
                    data-testid="clear-supplier-filters"
                  >
                    <X size={12} weight="bold" />
                    Clear all
                  </button>
                )}
              </div>

              <div className="p-5 space-y-5">
                {/* Search */}
                <div>
                  <FilterLabel>Search</FilterLabel>
                  <div className="relative">
                    <MagnifyingGlass
                      className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                      size={16}
                      style={{ color: "#FF7A00" }}
                    />
                    <Input
                      data-testid="supplier-search-input"
                      placeholder="Company or contact name…"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className="h-10 rounded-xl pl-9 text-sm"
                      style={{ background: "#FDFCF9", border: "1.5px solid #E8E3D9", color: "#333" }}
                    />
                  </div>
                </div>

                {/* City */}
                <div className="relative z-20">
                  <div className="mb-1.5 flex items-center justify-between gap-2">
                    <FilterLabel>City</FilterLabel>
                    {selectedCities.length > 0 ? (
                      <button
                        type="button"
                        onClick={clearCityFilter}
                        className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wide transition-colors hover:opacity-70"
                        style={{ color: "#FF7A00" }}
                        data-testid="supplier-filter-city-clear"
                      >
                        <X size={10} weight="bold" />
                        Clear
                      </button>
                    ) : null}
                  </div>
                  {selectedCities.length > 0 ? (
                    <div className="mb-2 flex flex-wrap gap-1.5">
                      {selectedCities.map((city) => (
                        <button
                          key={city}
                          type="button"
                          data-testid={`supplier-filter-city-chip-${city}`}
                          onClick={() => removeSelectedCity(city)}
                          className="inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors hover:opacity-80"
                          style={{
                            background: "rgba(255,122,0,0.1)",
                            borderColor: "rgba(255,122,0,0.25)",
                            color: "#FF7A00",
                          }}
                          title={`Remove ${city}`}
                        >
                          <MapPin size={11} weight="fill" />
                          <span className="truncate">{city}</span>
                          <X size={10} weight="bold" />
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <div className="relative">
                    <MagnifyingGlass
                      className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2"
                      size={16}
                      style={{ color: "#FF7A00" }}
                    />
                    <CityListAutocomplete
                      cities={cities}
                      value={citySearch}
                      onChange={setCitySearch}
                      onSelect={handleCitySelect}
                      excludeCities={selectedCities}
                      clearInputOnSelect
                      placeholder="Search and add cities…"
                      suggestionsPlacement="auto"
                      containerTestId="supplier-filter-city"
                      inputTestId="supplier-city-search"
                      inputClassName="pl-9"
                      inputStyle={{ background: "#FDFCF9", border: "1.5px solid #E8E3D9", color: "#333" }}
                    />
                  </div>
                  {selectedCities.length > 0 ? (
                    <p className="mt-1.5 text-[10px] font-semibold" style={{ color: "#FF7A00" }}>
                      Showing suppliers in {selectedCities.join(", ")}
                    </p>
                  ) : (
                    <p className="mt-1.5 text-[10px] font-medium" style={{ color: "#bbb" }}>
                      All cities — add one or more to filter
                    </p>
                  )}
                </div>

                {/* Supplier type */}
                <div>
                  <FilterLabel>Supplier type</FilterLabel>
                  <Select value={supplierType} onValueChange={setSupplierType}>
                    <SelectTrigger
                      data-testid="supplier-filter-type"
                      className={selectTriggerCls}
                      style={selectTriggerStyle}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      <SelectItem value="rental">Scaffolding rental</SelectItem>
                      <SelectItem value="sale">Scaffolding sale</SelectItem>
                      <SelectItem value="hybrid">Both (sale &amp; rental)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Sort */}
                <div>
                  <FilterLabel>Sort by</FilterLabel>
                  <Select value={sort} onValueChange={setSort}>
                    <SelectTrigger
                      data-testid="supplier-sort"
                      className={selectTriggerCls}
                      style={selectTriggerStyle}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name_asc">Name A–Z</SelectItem>
                      <SelectItem value="name_desc">Name Z–A</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </aside>

          {/* ── RESULTS GRID ────────────────────────────── */}
          <div>
            {/* Loading skeletons */}
            {loading && (
              <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 list-none p-0 m-0">
                {Array.from({ length: 6 }).map((_, i) => (
                  <li key={i}>
                    <div className="rounded-2xl overflow-hidden animate-pulse" style={{ background: "#fff", border: "1px solid #E8E3D9" }}>
                      <div className="h-[160px]" style={{ background: "#F0EBE3" }} />
                      <div className="p-5 space-y-3">
                        <div className="h-4 rounded-full w-2/3" style={{ background: "#F0EBE3" }} />
                        <div className="h-3 rounded-full w-1/2" style={{ background: "#F0EBE3" }} />
                        <div className="flex gap-1.5 mt-2">
                          <div className="h-5 w-16 rounded-full" style={{ background: "#F0EBE3" }} />
                          <div className="h-5 w-14 rounded-full" style={{ background: "#F0EBE3" }} />
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {/* Empty state */}
            {!loading && suppliers.length === 0 && (
              <div
                className="rounded-2xl py-20 flex flex-col items-center text-center"
                style={{ background: "#fff", border: "1.5px dashed #E8E3D9" }}
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                  style={{ background: "#F0EBE3" }}
                >
                  <Buildings size={32} weight="duotone" style={{ color: "#C4A882" }} />
                </div>
                <p className="font-black text-neutral-800 text-base mb-1">No suppliers match your filters</p>
                <p className="text-sm mb-6" style={{ color: "#aaa" }}>
                  Try clearing your search or choosing a different city.
                </p>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="px-5 py-2.5 rounded-full text-sm font-bold transition-colors hover:opacity-90"
                  style={{ background: "#FF7A00", color: "#fff" }}
                >
                  Reset filters
                </button>
              </div>
            )}

            {/* Supplier cards */}
            {!loading && suppliers.length > 0 && (
              <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 list-none p-0 m-0">
                {suppliers.map((s) => {
                  const displayName = s.name || s.company_name || "Supplier";
                  const sub = s.company_name && s.name && s.company_name !== s.name ? s.company_name : null;
                  const typeBadges = scaffoldingTypeBadges(s.supplier_type);
                  return (
                    <li key={s.id || `${displayName}-${(s.cities_served || []).join("-")}`}>
                      <div
                        className="rounded-2xl h-full overflow-hidden bg-white flex flex-col transition-all hover:-translate-y-0.5 hover:shadow-lg"
                        style={{ border: "1px solid #E8E3D9", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
                        data-testid="supplier-listing-card"
                      >
                        <SupplierCardLogo logoUrl={s.logo_url} name={displayName} />
                        <div className="p-4 sm:p-5 flex flex-col flex-1">
                          {/* Name + type badges */}
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="min-w-0">
                              <h2 className="font-black text-neutral-900 text-sm leading-snug truncate">{displayName}</h2>
                              {sub && <p className="text-xs truncate mt-0.5" style={{ color: "#aaa" }}>{sub}</p>}
                            </div>
                          </div>

                          {/* Type badges */}
                          {typeBadges.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-3">
                              {typeBadges.map((label) => (
                                <span
                                  key={label}
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold"
                                  style={{ background: "rgba(255,122,0,0.1)", color: "#FF7A00", border: "1px solid rgba(255,122,0,0.2)" }}
                                >
                                  {label}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Performance score */}
                          {typeof s.performance_score === "number" && s.performance_score >= 0 && (
                            <div className="flex items-center gap-1.5 mb-3">
                              <ChartBar size={13} weight="bold" style={{ color: "#FF7A00" }} />
                              <span className="text-xs font-semibold" style={{ color: "#888" }}>
                                {s.performance_score > 0 ? `Score ${s.performance_score}%` : "New supplier"}
                              </span>
                            </div>
                          )}

                          {/* Cities served */}
                          <div className="flex items-start gap-1.5 mt-auto">
                            <MapPin size={13} className="shrink-0 mt-0.5" weight="bold" style={{ color: "#C4A882" }} />
                            <div className="flex flex-wrap gap-1">
                              {(s.cities_served || []).length === 0 ? (
                                <span className="text-xs" style={{ color: "#aaa" }}>All India</span>
                              ) : (
                                (s.cities_served || []).slice(0, 5).map((c) => (
                                  <span
                                    key={c}
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium"
                                    style={{ background: "#F0EBE3", color: "#888", border: "1px solid #E8E3D9" }}
                                  >
                                    {c}
                                  </span>
                                ))
                              )}
                              {(s.cities_served || []).length > 5 && (
                                <span className="text-[10px] font-medium" style={{ color: "#bbb" }}>
                                  +{(s.cities_served || []).length - 5} more
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
